import { Link, usePage } from '@inertiajs/react'
import type { ReactNode } from 'react'
import { User, Building2, LogOut } from 'lucide-react'
import { cn } from '@/lib/utils'

const navItems = [
  { href: '/account', label: 'Profile', icon: User },
  { href: '/account/organization', label: 'Organization', icon: Building2 },
]

export default function AccountLayout({ children }: { children: ReactNode }) {
  const { url } = usePage()

  function isActive(href: string) {
    if (href === '/account') {
      return url === '/account'
    }
    return url.startsWith(href)
  }

  return (
    <div className="md:flex md:gap-10">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-52 md:flex-col md:shrink-0">
        <nav className="flex flex-col gap-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                isActive(item.href)
                  ? 'border-l-2 border-primary text-foreground font-medium rounded-none'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="mt-auto min-h-[300px] flex flex-col justify-end">
          <Link
            href="/logout"
            method="delete"
            as="button"
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Link>
        </div>
      </aside>

      {/* Mobile tab bar */}
      <div className="mb-6 flex items-center border-b border-border md:hidden">
        {navItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors',
              isActive(item.href)
                ? 'border-b-2 border-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </Link>
        ))}

        <div className="ml-auto">
          <Link
            href="/logout"
            method="delete"
            as="button"
            className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}
